import React, { useMemo, useState } from "react";
import {
  LuFileCode2,
  LuSearch,
  LuCopy,
  LuCheck,
  LuLock,
  LuUserRound,
  LuCoins,
  LuWallet,
  LuUsersRound,
  LuSettings,
  LuRadioTower,
  LuSmartphone,
  LuDownload,
} from "react-icons/lu";

/* ---- Ads endpoints generated from the 6 ad types ---- */
const adTypes = [
  { name: "Native Ads", base: "/api/app/ads/native", hasPublic: true },
  { name: "Full Screen Ads", base: "/api/app/ads/fullScreen", hasPublic: false },
  { name: "Banner Ads", base: "/api/app/ads/banner", hasPublic: false },
  { name: "Open Ads", base: "/api/app/ads/open", hasPublic: false },
  { name: "Reward Ads", base: "/api/app/ads/reward", hasPublic: false },
  { name: "In-House Ads", base: "/api/app/ads/inhouse", hasPublic: true },
];

const adEndpoints = adTypes.flatMap((t) => {
  const eps = [
    { method: "GET", path: `${t.base}/admin/get`, desc: `List all ${t.name} (admin)`, auth: "Admin" },
    { method: "POST", path: `${t.base}/add`, desc: `Create a ${t.name}`, auth: "Admin" },
    { method: "PUT", path: `${t.base}/update/:id`, desc: `Update a ${t.name}`, auth: "Admin" },
    { method: "DELETE", path: `${t.base}/admin/delete`, desc: `Delete a ${t.name}`, auth: "Admin" },
  ];
  if (t.hasPublic)
    eps.push({ method: "GET", path: `${t.base}/get/all`, desc: `Fetch ${t.name} for the app`, auth: "App" });
  return eps;
});

const API_GROUPS = [
  {
    title: "Mobile APK API",
    icon: <LuSmartphone className="text-lg" />,
    endpoints: [
      { method: "GET", path: "/api/controller/", desc: "App config — coins, limits, URLs, toggles", auth: "App" },
      { method: "GET", path: "/api/app/user/withdraw/card/get/all", desc: "Withdrawal amount options for the app", auth: "Public" },
      { method: "GET", path: "/api/app/ads/native/get/all", desc: "Native ads for the app", auth: "Public" },
      { method: "GET", path: "/api/app/ads/inhouse/get/all", desc: "In-house ads for the app", auth: "Public" },
    ],
  },
  {
    title: "Authentication & Admin",
    icon: <LuLock className="text-lg" />,
    endpoints: [
      { method: "POST", path: "/admin/login", desc: "Admin login — returns a JWT token", auth: "Public" },
      { method: "GET", path: "/admin/auth/check", desc: "Verify the admin token is valid", auth: "Admin" },
      { method: "PUT", path: "/admin/change/pw", desc: "Change the admin password", auth: "Admin" },
      { method: "GET", path: "/admin/dashbord", desc: "Dashboard analytics (users, plays, withdrawals)", auth: "Admin" },
    ],
  },
  {
    title: "App User (Mobile)",
    icon: <LuUserRound className="text-lg" />,
    endpoints: [
      { method: "POST", path: "/api/app/user/login", desc: "App user login / signup", auth: "Public" },
      { method: "GET", path: "/api/app/user/", desc: "Get logged-in user's profile & data", auth: "App" },
      { method: "POST", path: "/api/app/user/transfer/data", desc: "Migrate / transfer user data", auth: "Public" },
      { method: "PUT", path: "/api/app/user/update/coin", desc: "Update the user's coin balance", auth: "App" },
      { method: "GET", path: "/api/app/user/coin/history", desc: "User's coin transaction history", auth: "App" },
      { method: "GET", path: "/api/app/user/top/leaderbord", desc: "Top users leaderboard", auth: "App" },
      { method: "GET", path: "/api/controller/", desc: "App configuration (limits, urls, toggles)", auth: "App" },
    ],
  },
  {
    title: "Earn Coins (App)",
    icon: <LuCoins className="text-lg" />,
    endpoints: [
      { method: "POST", path: "/api/app/user/add/coin", desc: "Add coins from a game/scratch", auth: "App" },
      { method: "POST", path: "/api/app/user/referral/add/coin", desc: "Add referral reward coins", auth: "App" },
      { method: "POST", path: "/api/app/user/review/add/coin", desc: "Add review reward coins", auth: "App" },
      { method: "POST", path: "/api/app/user/platform/add/coin", desc: "Add share/platform reward coins", auth: "App" },
      { method: "POST", path: "/api/app/user/account/pub/coin/add", desc: "Add pub-game reward coins", auth: "App" },
    ],
  },
  {
    title: "Cash Tasks",
    icon: <LuCoins className="text-lg" />,
    endpoints: [
      { method: "GET", path: "/api/cashtask/", desc: "Web games / quizzes for the app's Home", auth: "App" },
      { method: "POST", path: "/api/cashtask/add/coin/:id", desc: "Pay a cash task: coin × seconds played ({ time })", auth: "App" },
      { method: "GET", path: "/api/cashtask/admin", desc: "List cash tasks", auth: "Admin" },
      { method: "POST", path: "/api/cashtask/admin", desc: "Add a cash task", auth: "Admin" },
      { method: "PUT", path: "/api/cashtask/admin/:id", desc: "Update a cash task", auth: "Admin" },
      { method: "DELETE", path: "/api/cashtask/admin", desc: "Delete cash tasks", auth: "Admin" },
    ],
  },
  {
    title: "Withdrawals (App)",
    icon: <LuWallet className="text-lg" />,
    endpoints: [
      { method: "POST", path: "/api/app/user/withdraw/upi", desc: "Request a UPI withdrawal", auth: "App" },
      { method: "POST", path: "/api/app/user/withdraw/paypal", desc: "Request a PayPal withdrawal", auth: "App" },
      { method: "POST", path: "/api/app/user/withdraw/binance", desc: "Request a Binance withdrawal", auth: "App" },
      { method: "POST", path: "/api/app/user/withdraw/bank", desc: "Request a bank withdrawal (older app versions)", auth: "App" },
      { method: "GET", path: "/api/app/user/withdraw/card/get/all", desc: "Available withdrawal methods/cards", auth: "Public" },
    ],
  },
  {
    title: "User Management (Admin)",
    icon: <LuUsersRound className="text-lg" />,
    endpoints: [
      { method: "GET", path: "/api/app/user/admin", desc: "List all users (paginated, filterable)", auth: "Admin" },
      { method: "DELETE", path: "/api/app/user/admin", desc: "Delete one or more users", auth: "Admin" },
      { method: "GET", path: "/api/app/user/admin/excel", desc: "Export users as Excel", auth: "Admin" },
      { method: "PUT", path: "/api/app/user/admin/block/:id", desc: "Block / unblock a user", auth: "Admin" },
      { method: "PUT", path: "/api/app/user/profile/admin/:id", desc: "Update a user's profile", auth: "Admin" },
      { method: "GET", path: "/api/app/user/admin/top/leaderbord", desc: "Leaderboard (admin view)", auth: "Admin" },
    ],
  },
  {
    title: "Withdrawal Management (Admin)",
    icon: <LuWallet className="text-lg" />,
    endpoints: [
      { method: "GET", path: "/api/app/user/admin/withdraw", desc: "List withdrawal requests", auth: "Admin" },
      { method: "PUT", path: "/api/app/user/admin/withdraw", desc: "Approve a withdrawal request", auth: "Admin" },
      { method: "DELETE", path: "/api/app/user/admin/withdraw", desc: "Delete a withdrawal request", auth: "Admin" },
      { method: "GET", path: "/api/app/user/withdraw/card/admin/get", desc: "List withdrawal cards", auth: "Admin" },
      { method: "POST", path: "/api/app/user/withdraw/card/add", desc: "Add a withdrawal card", auth: "Admin" },
      { method: "PUT", path: "/api/app/user/withdraw/card/update/:id", desc: "Update a withdrawal card", auth: "Admin" },
      { method: "DELETE", path: "/api/app/user/withdraw/card/admin/delete", desc: "Delete a withdrawal card", auth: "Admin" },
    ],
  },
  {
    title: "Controller / Settings (Admin)",
    icon: <LuSettings className="text-lg" />,
    endpoints: [
      { method: "GET", path: "/api/controller/admin", desc: "Get all controller settings", auth: "Admin" },
      { method: "PUT", path: "/api/controller/admin/update/field/coin", desc: "Update a coin/limit value", auth: "Admin" },
      { method: "PUT", path: "/api/controller/admin/update/status", desc: "Update feature toggles / urls / app ids", auth: "Admin" },
      { method: "PUT", path: "/api/controller/admin/update/range/scratch", desc: "Update scratch reward range", auth: "Admin" },
      { method: "PUT", path: "/api/controller/admin/update/range/tictactoe", desc: "Update Captcha / TicTacToe reward range", auth: "Admin" },
      { method: "PUT", path: "/api/controller/admin/facebook/change", desc: "Update Facebook SDK credentials", auth: "Admin" },
    ],
  },
  {
    title: "Ads Management (Admin)",
    icon: <LuRadioTower className="text-lg" />,
    endpoints: adEndpoints,
  },
  {
    title: "Promo Apps & Install Referral",
    icon: <LuDownload className="text-lg" />,
    endpoints: [
      { method: "GET", path: "/api/app/promo/apps/get/all", desc: "Installable apps shown in Rewarded Play", auth: "App" },
      { method: "POST", path: "/api/app/referral/click", desc: "Issue an install token + Play Store URL", auth: "App" },
      { method: "GET", path: "/api/app/referral/my", desc: "User's install referral history", auth: "App" },
      { method: "POST", path: "/api/app/referral/complete", desc: "Called by the INSTALLED app on home screen — credits coins", auth: "Public" },
      { method: "GET", path: "/api/app/promo/apps/admin/get", desc: "List all promo apps", auth: "Admin" },
      { method: "POST", path: "/api/app/promo/apps/admin/fetch-meta", desc: "Read package/name/icon from a Play Store URL", auth: "Admin" },
      { method: "POST", path: "/api/app/promo/apps/add", desc: "Add a promo app (multipart — icon upload)", auth: "Admin" },
      { method: "PUT", path: "/api/app/promo/apps/update/:id", desc: "Update a promo app", auth: "Admin" },
      { method: "DELETE", path: "/api/app/promo/apps/admin/delete", desc: "Delete a promo app (?id=)", auth: "Admin" },
      { method: "GET", path: "/api/app/referral/admin/installs", desc: "Install report — status, coins paid, fraud blocks", auth: "Admin" },
    ],
  },
];

const methodStyles = {
  GET: "bg-teal-500/15 text-brand-teal border border-teal-400/20",
  POST: "bg-blue-500/15 text-blue-300 border border-blue-400/20",
  PUT: "bg-amber-500/15 text-amber-300 border border-amber-400/20",
  DELETE: "bg-red-500/15 text-red-300 border border-red-400/20",
};

const authStyles = {
  Admin: "bg-purple-500/15 text-purple-300",
  App: "bg-cyan-500/15 text-cyan-700",
  Public: "bg-white/10 text-main-subfont",
};

const ApiDocs = () => {
  const [query, setQuery] = useState("");
  const [copied, setCopied] = useState("");

  const baseUrl =
    typeof window !== "undefined" ? window.location.origin : "";

  const totalEndpoints = useMemo(
    () => API_GROUPS.reduce((n, g) => n + g.endpoints.length, 0),
    []
  );

  const filteredGroups = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return API_GROUPS;
    return API_GROUPS.map((g) => ({
      ...g,
      endpoints: g.endpoints.filter(
        (e) =>
          e.path.toLowerCase().includes(q) ||
          e.desc.toLowerCase().includes(q) ||
          e.method.toLowerCase().includes(q) ||
          g.title.toLowerCase().includes(q)
      ),
    })).filter((g) => g.endpoints.length > 0);
  }, [query]);

  const copyPath = async (path) => {
    try {
      await navigator.clipboard.writeText(baseUrl + path);
      setCopied(path);
      setTimeout(() => setCopied(""), 1500);
    } catch {
      /* ignore */
    }
  };

  const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="md:ml-56 px-4 sm:px-6 lg:px-8 mt-16 pb-12">
      {/* Header */}
      <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 pt-6 pb-5">
        <div className="flex items-center gap-4">
          <span className="p-3.5 bg-gradient-to-br from-teal-500 to-cyan-500 text-white rounded-2xl shadow-lg shadow-teal-500/30 flex items-center justify-center">
            <LuFileCode2 className="text-2xl md:text-3xl" />
          </span>
          <div>
            <h1 className="text-2xl md:text-3xl font-extrabold text-main-font tracking-tight leading-none">
              API Documentation
            </h1>
            <p className="text-sm text-main-subfont mt-1.5">
              All {totalEndpoints} endpoints used by the app & admin panel
            </p>
          </div>
        </div>

        {/* Search */}
        <div className="relative w-full xl:w-80">
          <LuSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-main-subfont z-10" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search endpoints..."
            className="w-full pl-10 pr-4 py-2.5 rounded-xl text-sm bg-white/5 border border-white/10 text-main-font placeholder-main-subfont outline-none transition focus:border-teal-400 focus:ring-2 focus:ring-teal-400/30"
          />
        </div>
      </div>

      {/* Base URL + legend */}
      <div className="flex flex-wrap items-center gap-3 pb-5 border-b border-white/5 text-xs">
        <span className="text-main-subfont">Base URL</span>
        <code className="px-2.5 py-1 rounded-lg bg-white/5 border border-white/10 text-brand-teal font-mono">
          {baseUrl || "https://your-domain.com"}
        </code>
        <span className="mx-1 h-4 w-px bg-white/10 hidden sm:block" />
        <span className={`px-2 py-1 rounded-md font-semibold ${authStyles.Admin}`}>Admin token</span>
        <span className={`px-2 py-1 rounded-md font-semibold ${authStyles.App}`}>App token</span>
        <span className={`px-2 py-1 rounded-md font-semibold ${authStyles.Public}`}>Public</span>
      </div>

      {/* Quick-jump chip bar (sticky) */}
      <div className="sticky top-16 z-20 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-3 bg-night-950/85 backdrop-blur-xl border-b border-white/5 mt-4">
        <div className="flex gap-2 overflow-x-auto pb-0.5">
          {API_GROUPS.map((g) => (
            <button
              key={g.title}
              onClick={() => scrollTo(slug(g.title))}
              className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-white/5 border border-white/10 text-main-subfont hover:border-teal-400/50 hover:text-brand-teal hover:bg-teal-400/5 transition-colors whitespace-nowrap"
            >
              {g.title}
              <span className="text-[10px] opacity-70">{g.endpoints.length}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Groups */}
      <div className="space-y-5 mt-6">
        {filteredGroups.length === 0 ? (
          <p className="text-center text-main-subfont py-16">
            No endpoints match “{query}”.
          </p>
        ) : (
          filteredGroups.map((group) => (
            <section
              key={group.title}
              id={slug(group.title)}
              className="ui-card-solid p-5 sm:p-6 scroll-mt-32"
            >
              <div className="flex items-center gap-3 mb-4 pb-4 border-b border-white/5">
                <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500/20 to-cyan-500/10 text-brand-teal flex items-center justify-center shrink-0 border border-teal-400/20">
                  {group.icon}
                </span>
                <h2 className="text-base sm:text-lg font-bold text-main-font">
                  {group.title}
                </h2>
                <span className="ml-auto inline-flex items-center justify-center min-w-[1.75rem] h-6 px-2 rounded-full bg-white/5 border border-white/10 text-xs font-semibold text-main-subfont">
                  {group.endpoints.length}
                </span>
              </div>

              <div className="space-y-2">
                {group.endpoints.map((e) => (
                  <div
                    key={e.method + e.path}
                    className="group rounded-xl bg-white/[0.03] hover:bg-white/[0.06] border border-white/5 hover:border-brand-teal/20 px-3.5 py-3 transition-all"
                  >
                    <div className="flex items-center gap-3">
                      <span
                        className={`shrink-0 w-16 text-center px-2 py-1 rounded-md text-[11px] font-bold tracking-wide ${
                          methodStyles[e.method] || methodStyles.GET
                        }`}
                      >
                        {e.method}
                      </span>

                      <button
                        onClick={() => copyPath(e.path)}
                        title="Copy full URL"
                        className="flex-1 flex items-center gap-2 font-mono text-xs sm:text-sm text-main-font hover:text-brand-teal transition-colors text-left min-w-0"
                      >
                        <span className="truncate">{e.path}</span>
                        {copied === e.path ? (
                          <LuCheck className="shrink-0 text-brand-teal" size={14} />
                        ) : (
                          <LuCopy
                            className="shrink-0 opacity-0 group-hover:opacity-100 text-main-subfont"
                            size={14}
                          />
                        )}
                      </button>

                      <span
                        className={`shrink-0 px-2 py-1 rounded-md text-[10px] font-semibold ${
                          authStyles[e.auth] || authStyles.Public
                        }`}
                      >
                        {e.auth}
                      </span>
                    </div>

                    {e.desc && (
                      <p className="text-xs text-main-subfont mt-1.5 pl-[4.75rem]">
                        {e.desc}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </section>
          ))
        )}
      </div>
    </div>
  );
};

export default ApiDocs;
